<template>
  <div
    class="section"
    :class="{
      'mb-3': !last,
    }"
  >
    <template v-if="index === 0">
      <DescriptionEditorContainer
        v-if="header"
        v-show="header && !shouldHideIfEmpty({
          type: header.type, sections: header.sections, isTemplate,
        })"
        class="header-editor"
        :field-index="fieldIndex - 1"
        :is-editable="isEditable"
        :is-template="isTemplate"
        @input-editor="onDescriptionEditorUpdate(fieldIndex - 1, $event)"
        @create="onDescriptionEditorCreate(fieldIndex - 1)"
        @copy-content="onCopyContent"
        @paste-content="onPasteContent"
        @track-view-highlight="onViewHighlightTracking"
        @track-apply-highlight="onApplyHighlightTracking"
        @track-view-nudge="onViewNudgeTracking"
        @track-apply-nudge="onApplyNudgeTracking"
        @lock-change="onLockChange(fieldIndex - 1, $event)"
      />
      <DescriptionEditorContainer
        class="main-editor"
        :field-index="fieldIndex"
        :is-editable="isEditable"
        :is-template="isTemplate"
        @input-editor="onDescriptionEditorUpdate(fieldIndex, $event)"
        @create="onDescriptionEditorCreate(fieldIndex)"
        @copy-content="onCopyContent"
        @paste-content="onPasteContent"
        @track-view-highlight="onViewHighlightTracking"
        @track-apply-highlight="onApplyHighlightTracking"
        @track-view-nudge="onViewNudgeTracking"
        @track-apply-nudge="onApplyNudgeTracking"
        @lock-change="onLockChange(fieldIndex, $event)"
      />
      <DescriptionEditorContainer
        v-if="footer"
        v-show="footer && !shouldHideIfEmpty({
          type: footer.type, sections: footer.sections, isTemplate,
        })"
        class="footer-editor"
        :field-index="fieldIndex + 1"
        :is-editable="isEditable"
        :is-template="isTemplate"
        @input-editor="onDescriptionEditorUpdate(fieldIndex + 1, $event)"
        @create="onDescriptionEditorCreate(fieldIndex + 1)"
        @copy-content="onCopyContent"
        @paste-content="onPasteContent"
        @track-view-highlight="onViewHighlightTracking"
        @track-apply-highlight="onApplyHighlightTracking"
        @track-view-nudge="onViewNudgeTracking"
        @track-apply-nudge="onApplyNudgeTracking"
        @lock-change="onLockChange(fieldIndex + 1, $event)"
      />
    </template>
  </div>
</template>

<script>
/* eslint-disable vue/require-prop-types */
import { shouldHideIfEmpty } from '@/helpers/constants/hideEmptyFields';

import { mapState, mapActions } from 'pinia';
import DescriptionEditorContainer
  from '@/components/document/descriptionEditor/DescriptionEditorContainer.vue';
import { useTracking } from '@/stores/tracking/tracking';
import { useEditor } from '@/stores/editor/editor';

export default {
  name: 'TrSection',
  components: { DescriptionEditorContainer },
  props: [
    'id',
    'fieldId',
    'fieldIndex',
    'fieldType',
    'index',
    'blocks',
    'first',
    'last',
    'only',
    'type',
    'formatBlacklist',
    'lockType',
    'isEditable',
    'job',
    'description',
    'updateAndSave',
    'editorsInitState',
    'editorInitializedAction',
    'isTemplate',
    'addSection',
    'deleteSection',
    'prevSection',
    'nextSection',
    'focusSection',
    'swapSections',
    'sectionTypes',
    'focus',
    'sectionNudges',
    'blockNudges',
  ],
  data() {
    return {
      nudgeDivHeight: '0px',
    };
  },
  computed: {
    ...mapState(useEditor, ['fields', 'bodyMessages']),
    header() {
      const prev = this.fields[this.fieldIndex - 1];
      return prev && prev.type === `${this.fieldType}__header` ? prev : null;
    },
    footer() {
      const next = this.fields[this.fieldIndex + 1];
      return next && next.type === `${this.fieldType}__footer` ? next : null;
    },
  },
  methods: {
    shouldHideIfEmpty,
    ...mapActions(useEditor, [
      'copyToClipboard',
      'setSectionType',
      'setSectionLockType',
      'updateFieldSections',
    ]),

    getBlocksWithLock({ blocks, isLocked }) {
      return blocks.map((block) => ({
        ...block, lockType: isLocked ? 'content' : false,
      }));
    },

    onDescriptionEditorUpdate(fieldIndex, { blocks, isLocked }) {
      if (!this.editorsInitState.description) {
        return;
      }

      const sections = [{
        id: this.id,
        type: 'default',
        lockType: this.lockType,
        formatBlacklist: this.formatBlacklist,
        blocks: [],
      }];

      this.getBlocksWithLock({ blocks, isLocked }).forEach((block) => {
        // non lever_list field: push all blocks into default section
        sections[0].blocks.push(block);
      });

      this.updateFieldSections({ fieldIndex, sections });
      this.updateAndSave();
    },

    onLockChange(fieldIndex, { isLocked }) {
      this.updateFieldSections({
        fieldIndex,
        sections: [{
          id: this.id,
          type: 'default',
          lockType: this.lockType,
          formatBlacklist: this.formatBlacklist,
          blocks: this.getBlocksWithLock({ blocks: this.blocks, isLocked }),
        }],
      });
      this.updateAndSave();
    },

    onDescriptionEditorCreate(fieldIndex) {
      const field = this.fields[fieldIndex];
      (field.sections || []).forEach((section, sectionIndex) => {
        (section.blocks || []).forEach((block, blockIndex) => {
          this.editorInitializedAction('description', fieldIndex, sectionIndex, blockIndex);
        });
      });
    },

    onCopyContent() {
      useTracking().editorAction({ action: 'Copy event' });
    },

    onPasteContent() {
      useTracking().editorAction({ action: 'Paste event' });
    },

    onViewHighlightTracking(event) {
      useTracking().viewHighlight({
        job: this.job,
        props: event,
      });
    },

    onApplyHighlightTracking(event) {
      const {
        suggestion = '', action, highlightedText, readMoreUrl, type, className, suggestions,
      } = event;
      if (readMoreUrl && type === 'readMore') {
        useTracking().readMoreLinkClicked({
          job: this.job,
          props: {
            link: readMoreUrl,
            action,
            replacement: suggestion,
            removedText: highlightedText,
            suggestions,
          },
        });
        return;
      }
      useTracking().applyHighlight({
        job: this.job,
        props: {
          action,
          replacement: suggestion,
          removedText: highlightedText,
          color: className,
          suggestions,
        },
      });
    },

    onViewNudgeTracking(props) {
      useTracking().viewNudge({ job: this.job, props });
    },

    onApplyNudgeTracking(props) {
      useTracking().applyNudge({ job: this.job, props });
    },
  },
};
</script>

<style lang="less" scoped>
.section {
  word-break: break-word;
}
.icon {
  cursor: pointer;
  color: #808080;
}
.v-list-item--disabled .v-list-item__title {
  color: #C8C8C8!important;
}
.v-btn:focus::before {
    opacity: 0 !important;
}
.v-list-item__title {
  font-size: 13px !important;
  font-weight: 400 !important;
  color: #77818F !important;
}
.field-label {
  position: absolute;
  top: -20px;
  right: 28px;
}
.v-list-item--link:before {
  background-color: @text-brand;
}
.v-btn:before {
  background-color: @text-brand;
}
.v-btn.v-btn--active:before {
  background-color: currentColor;
  opacity: .05;
}
.header-editor, .main-editor, .footer-editor {
  padding: 0 24px 0 24px;
}
.footer-editor {
  padding-bottom: 24px;
  margin-bottom: -24px;
  padding-top: 8px;
}
.header-editor {
  padding-bottom: 12px;
  margin-top: 4px;
}
</style>
